import React, { useEffect, useState, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
import { generateReportApi } from "../../api/reportApi";

export default function ReportResultPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const dt = useRef(null);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get("id");
    
    const stateData = useMemo(() => {
        let data = location.state;
        if (!data && id) {
            const localData = localStorage.getItem(`reportResultState_${id}`);
            if (localData) {
                data = JSON.parse(localData);
            }
        }
        return data;
    }, [location.state, id]);
    
    const { payload, reportName, reportType, appliedFilters, groupByText } = stateData || {};

    useEffect(() => {
        if (!payload || !payload.spName) {
            setError("No report query parameters found. Please start from the reports selector.");
            setLoading(false);
            return;
        }
        fetchReportData();
    }, [payload]);

    const fetchReportData = async () => {
        setLoading(true);
        setError("");
        try {
            const res = await generateReportApi(payload);
            setData(res || []);
        } catch (err) {
            console.error("Generate report API error:", err);
            setError(err?.response?.data?.message || "Failed to load report data from the database.");
        } finally {
            setLoading(false);
        }
    };

    const exportCSV = () => {
        if (dt.current) {
            dt.current.exportCSV();
        }
    };

    const formatHeader = (key) => {
        return key
            .replace(/_/g, " ")
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .split(" ")
            .filter(Boolean)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
    };

    const dynamicColumns = data.length > 0 
        ? Object.keys(data[0])
            .filter(key => key.toLowerCase() !== "style_id")
            .map(key => (
                <Column 
                    key={key} 
                    field={key} 
                    header={formatHeader(key)} 
                    sortable 
                    filter
                    filterPlaceholder={`Filter ${formatHeader(key)}`}
                    style={{ minWidth: "150px" }}
                />
            ))
        : [];

    return (
        <div className="report-result-page w-100" style={{ padding: "20px 0" }}>
            {/* Header Card */}
            <div className="enquiry-header-card mb-4" style={{
                background: "#ffffff",
                borderRadius: "16px",
                padding: "18px 24px",
                boxShadow: "0 10px 30px rgba(17, 38, 146, 0.05)",
                border: "1px solid rgba(226, 232, 240, 0.8)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                    <button 
                        className="back-btn" 
                        onClick={() => navigate("/report")} 
                        style={{
                            background: "#f1f5f9",
                            border: "none",
                            borderRadius: "50%",
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            transition: "background 0.2s"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#e2e8f0"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "#f1f5f9"}
                    >
                        <i className="pi pi-arrow-left" style={{ color: "#475569", fontWeight: "bold" }} />
                    </button>
                    <div>
                        <h1 className="enquiry-page-title" style={{
                            margin: 0,
                            fontSize: "20px",
                            fontWeight: "700",
                            color: "#0f172a"
                        }}>
                            {reportName || "Generated Report"}
                        </h1>
                        <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "500", marginTop: "4px" }}>
                            <span style={{ textTransform: "capitalize" }}>Type: {reportType || "Summary"}</span>
                            {groupByText && (
                                <span style={{ marginLeft: "12px" }}>
                                    • Grouped By: <strong style={{ color: "#1e293b", fontWeight: "600" }}>{groupByText}</strong>
                                </span>
                            )}
                            {appliedFilters && appliedFilters.length > 0 && (
                                <div style={{ marginTop: "4px", fontSize: "11px", color: "#475569", lineHeight: "1.4" }}>
                                    <span style={{ fontWeight: "600" }}>Filters: </span>
                                    {appliedFilters.join(" | ")}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <Button 
                        label="Export CSV" 
                        icon="pi pi-file-excel" 
                        className="btn-primary" 
                        onClick={exportCSV}
                        disabled={loading || data.length === 0}
                    />
                    <Button 
                        label="Refresh" 
                        icon="pi pi-refresh" 
                        className="btn-secondary-outline" 
                        onClick={fetchReportData}
                        disabled={loading}
                    />
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="alert alert-danger shadow-sm rounded-3 mb-4" role="alert" style={{
                    borderRadius: "12px",
                    padding: "16px 20px"
                }}>
                    <i className="pi pi-exclamation-triangle me-2" />
                    <strong>Error:</strong> {error}
                </div>
            )}

            {/* Table Area */}
            <Card className="report-card p-card shadow-sm" style={{
                borderRadius: "16px",
                border: "1px solid rgba(226, 232, 240, 0.8)",
                background: "#ffffff",
                boxShadow: "0 10px 30px rgba(17, 38, 146, 0.04)"
            }}>
                {loading ? (
                    <div style={{ padding: "20px" }}>
                        <div className="d-flex justify-content-between mb-4">
                            <Skeleton width="25%" height="2rem" />
                            <Skeleton width="15%" height="2rem" />
                        </div>
                        <Skeleton width="100%" height="3rem" className="mb-2" />
                        <Skeleton width="100%" height="2rem" className="mb-2" />
                        <Skeleton width="100%" height="2rem" className="mb-2" />
                        <Skeleton width="100%" height="2rem" className="mb-2" />
                        <Skeleton width="100%" height="2rem" />
                    </div>
                ) : data.length === 0 ? (
                    <div style={{
                        padding: "60px 20px",
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "14px"
                    }}>
                        <div style={{
                            background: "#eff6ff",
                            borderRadius: "50%",
                            width: "64px",
                            height: "64px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}>
                            <i className="pi pi-search" style={{ color: "#3b82f6", fontSize: "24px" }} />
                        </div>
                        <h3 style={{ margin: 0, fontSize: "16px", color: "#1e293b", fontWeight: "600" }}>
                            No Records Found
                        </h3>
                        <p style={{ margin: 0, fontSize: "13px", color: "#64748b", maxWidth: "400px", lineHeight: "1.5" }}>
                            The Stored Procedure returned zero records for the specified filter parameters. Please adjust dates or filters in the Report selector.
                        </p>
                        <Button 
                            label="Go Back to Filters" 
                            icon="pi pi-arrow-left" 
                            className="p-button-outlined p-button-sm mt-2" 
                            onClick={() => navigate("/report")}
                            style={{
                                borderRadius: "8px",
                                fontSize: "13px",
                                padding: "8px 14px"
                            }}
                        />
                    </div>
                ) : (
                    <div className="p-fluid">
                        <DataTable 
                            ref={dt}
                            value={data} 
                            paginator 
                            rows={10} 
                            rowsPerPageOptions={[10, 20, 50, 100]}
                            removableSort
                            stripedRows
                            responsiveLayout="scroll"
                            showGridlines
                            size="small"
                            className="p-datatable-sm"
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                            style={{ fontSize: "13px" }}
                            exportFilename={reportName || "Report"}
                        >
                            {dynamicColumns}
                        </DataTable>
                    </div>
                )}
            </Card>
        </div>
    );
}
